/**
 * Entrada.
 *
 * Um campo só para o identificador: o barbeiro digita o usuário dele, o cliente
 * digita o telefone. Obrigar a escolher "entrar com telefone" ou "entrar com
 * usuário" seria uma pergunta que o app consegue responder sozinho.
 *
 * O formulário sai daqui como peça própria porque a home é ele: a primeira tela
 * da barbearia é entrar, não uma vitrine com um link de login no rodapé.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TenantMark } from '../components/TenantMark.tsx';
import { Button, ErrorNote, Field } from '../components/ui.tsx';
import { ApiError, api } from '../lib/api.ts';
import { useRefreshSession, useTenant } from '../lib/contexts.tsx';
import { biometricsAvailable, signInWithPasskey, wasDismissed } from '../lib/passkey.ts';

export function SignInForm() {
  const navigate = useNavigate();
  const refresh = useRefreshSession();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/auth/sign-in', { identifier, password });
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setLoading(false);
    }
  }

  async function enter() {
    await refresh();
    navigate('/', { replace: true });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <BiometricSignIn onEntered={enter} />

      <Field
        label="Telefone ou usuário"
        value={identifier}
        onChange={(event) => setIdentifier(event.target.value)}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        inputMode="text"
        required
      />

      <Field
        label="Senha"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        required
      />

      {error && <ErrorNote>{error.message}</ErrorNote>}

      <Button type="submit" full loading={loading}>
        Entrar
      </Button>

      <p className="text-center text-[13px] text-ink-faint">
        Esqueceu a senha? Fale com a barbearia — ela gera um link novo para você pelo WhatsApp.
      </p>
    </form>
  );
}

/**
 * Entrar com Face ID, Touch ID ou digital.
 *
 * Fica *acima* dos campos porque, para quem já registrou o aparelho, é o único
 * gesto da tela: um toque, sem teclado. Quem nunca registrou nem vê este bloco —
 * a checagem é do aparelho, não da conta, e sem HTTPS o navegador nem expõe a
 * API, então o botão nunca aparece onde não teria como funcionar.
 *
 * Desistir do Face ID não é erro: fechar a folha do sistema com o dedo devolve
 * `NotAllowedError`, e pintar isso de vermelho ensinaria a pessoa a temer o
 * botão que ela mesma cancelou.
 */
function BiometricSignIn({ onEntered }: { onEntered: () => Promise<void> }) {
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void biometricsAvailable().then((supported) => {
      if (alive) setAvailable(supported);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!available) return null;

  async function start() {
    setLoading(true);
    setError(null);

    try {
      await signInWithPasskey();
      await onEntered();
    } catch (caught) {
      if (!wasDismissed(caught)) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Não foi possível entrar com a biometria. Use sua senha.',
        );
      }
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Button variant="secondary" full loading={loading} onClick={start}>
        Entrar com Face ID ou digital
      </Button>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-[12px] text-ink-faint">ou entre com sua senha</span>
        <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}

/** A rota `/entrar`, para quem chega por link direto ou é mandado para cá. */
export function SignIn() {
  const { tenant } = useTenant();

  return (
    <div className="pb-safe mx-auto flex min-h-full w-full max-w-sm flex-col justify-center gap-6 px-6 py-10">
      <div className="flex flex-col items-center gap-3 text-center">
        <TenantMark size={64} />
        <h1 className="text-[22px] font-bold text-ink">{tenant.name}</h1>
      </div>

      <SignInForm />

      <p className="text-center text-[14px]">
        <Link to="/cadastro" className="font-medium text-accent">
          Criar conta
        </Link>
      </p>
    </div>
  );
}
